import * as PropTypes from 'prop-types';
import * as React from 'react';
import { each, filter } from 'lodash';
import BundleInfoForm from './bundle_info_form';
import ProductDetails from './product_details';
import Loading from '../shared/loading';
import ProductBundlePreview from './product_bundle_preview';
import CSRFForm from '../shared/csrf_form';
import BundledProductValidator from './bundled_product_validator';
import {
  APIRequest,
  ProductSearchForm,
} from '@reverbdotcom/discovery-ui';

class ProductBundleForm extends React.Component {
  static propTypes = {
    productBundlePath: PropTypes.string.isRequired,
    productBundlesPath: PropTypes.string.isRequired,
    userCurrency: PropTypes.string.isRequired,
    deleteProductBundlePath: PropTypes.string,
    disableSearchFormDebounce: PropTypes.bool,
  };

  state = {
    productBundle: null,
    removedBundledProducts: [],
  };

  componentDidMount() {
    this.fetchProductBundle();
  }

  submitText = () => {
    if (this.state.productBundle.id) {
      return I18n.t('js.productBundles.bundleInfoForm.updateBundle');
    }
    return I18n.t('js.productBundles.bundleInfoForm.createBundle');
  };

  deleteButton = () => {
    if (this.state.productBundle.id) {
      return (
        <a
          href={this.props.deleteProductBundlePath}
          className="button tablet-d-block tablet-mt-1"
          data-method="delete"
          data-confirm={I18n.t('js.productBundles.bundleInfoForm.confirm')}
        >
          {I18n.t('js.productBundles.bundleInfoForm.deleteBundle')}
        </a>
      );
    }
  };

  fetchProductBundle = () => {
    APIRequest.V3.get(this.props.productBundlePath).then((response) => {
      this.setState({
        productBundle: response,
      });
    });
  };

  productSelected = (product) => {
    const newListing = product;
    newListing.original_price = product.price;
    newListing.price = {
      symbol: product.price.symbol,
      amount: '0',
    };
    newListing.buyer_price = Object.assign(product.buyer_price, {
      amount: '0',
    });

    this.state.productBundle.bundled_products.push({ listing: newListing });
    this.setState({
      productBundle: this.state.productBundle,
      searching: false,
    });
  };

  removeProduct = (index) => {
    const removedBundledProduct = this.state.productBundle.bundled_products[index];
    if (removedBundledProduct.id) {
      this.state.removedBundledProducts.push(removedBundledProduct);
    }
    this.state.productBundle.bundled_products.splice(index, 1);
    this.setState({
      productBundle: this.state.productBundle,
      removedBundledProducts: this.state.removedBundledProducts,
    });
  };

  removedBundledProducts = () =>
    this.state.removedBundledProducts.map((bundledProduct, index) => (
      <div key={bundledProduct.id}>
        <input
          type="hidden"
          value={bundledProduct.id}
          name={`product_bundle[bundled_products_attributes][${index +
            this.state.productBundle.bundled_products.length}][id]`}
        />
        <input
          type="hidden"
          value="1"
          name={`product_bundle[bundled_products_attributes][${index +
            this.state.productBundle.bundled_products.length}][_destroy]`}
        />
      </div>
    ));

  renderPreview = () => {
    if (!this.hasListings()) {
      return false;
    }

    return (
      <div>
        <h4 className="heading-4 mt-1 mb-half">
          {I18n.t('js.productBundles.productBundleForm.preview')}
        </h4>
        <div className="module bd-rounded mb-1">
          <ProductBundlePreview productBundle={this.state.productBundle} />
        </div>
      </div>
    );
  };

  anyBundledProducts = callback =>
    filter(this.state.productBundle.bundled_products, callback).length;

  hasListings = () => this.anyBundledProducts(bundledProduct => bundledProduct.listing);

  validateForm = () => {
    each(this.state.productBundle.bundled_products, (bundledProduct) => {
      new BundledProductValidator(bundledProduct).validate();
    });
    this.setState({ productBundle: this.state.productBundle });
  };

  errorsPresent = () => this.anyBundledProducts(bundledProduct => bundledProduct.errorMessage);

  handleSubmit = (event) => {
    this.validateForm();
    if (this.errorsPresent()) {
      event.preventDefault();
    }
  };

  bundledProductChanged = (bundledProduct, index) => {
    this.state.productBundle.bundled_products[index] = bundledProduct;
    this.setState({ productBundle: this.state.productBundle });
  };

  addListing = () => {
    this.setState({ searching: true });
  };

  renderBundledProducts = () =>
    this.state.productBundle.bundled_products.map((bundledProduct, index) => (
      <BundleInfoForm
        bundledProduct={bundledProduct}
        remove={this.removeProduct}
        userCurrency={this.props.userCurrency}
        bundledProductChanged={this.bundledProductChanged}
        index={index}
        key={index}
      />
    ));

  currentListingIds = () => {
    const currentListingIds = this.state.productBundle.bundled_products.map(bundledProduct => bundledProduct.listing.id);
    currentListingIds.push(this.state.productBundle.main_listing.id);

    return currentListingIds;
  };

  renderSearchForm = () => {
    if (!this.hasListings() || this.state.searching) {
      return (
        <div className="module">
          <ProductSearchForm
            productSelected={this.productSelected}
            currentListingIds={this.currentListingIds()}
            labelText={I18n.t('js.productBundles.productSearchForm.label')}
            disableDebounce={this.props.disableSearchFormDebounce}
          />
        </div>
      );
    }
  };

  formButtons = () => {
    if (this.hasListings()) {
      return (
        <div className="mt-1">
          <input
            type="submit"
            value={this.submitText()}
            className="button button--orange mr-1 tablet-width-100 tablet-d-block tablet-mt-1 tablet-mr-0"
          />
          <a
            onClick={this.addListing}
            className="button mr-1 tablet-d-block tablet-mt-1 tablet-mr-0"
          >
            {I18n.t('js.productBundles.productBundleForm.addItem')}
          </a>
          {this.deleteButton()}
        </div>
      );
    }
  };

  render() {
    if (!this.state.productBundle) {
      return (
        <div className="ptb-6">
          <Loading />
        </div>
      );
    }

    return (
      <CSRFForm method="POST" action={this.props.productBundlesPath} onSubmit={this.handleSubmit}>
        <div className="g-container pb-6">
          <div className="g-col-8 g-col-mobile-12 mobile-mb-2">
            <div className="module bd-rounded bundle-form mb-1">
              <h4 className="heading-3 mb-half">
                {I18n.t('js.productBundles.productBundleForm.mainListing')}
              </h4>
              <ProductDetails product={this.state.productBundle.main_listing} />
              <input
                type="hidden"
                value={this.state.productBundle.main_listing.id}
                name="product_bundle[main_listing_id]"
              />
            </div>
            {this.removedBundledProducts()}
            {this.renderBundledProducts()}
            {this.renderSearchForm()}
            {this.formButtons()}
          </div>
          <div className="g-col-4 g-col-mobile-12">
            <h4 className="heading-4 mb-half">
              {I18n.t('js.productBundles.productBundleForm.about')}
            </h4>
            <ul className="fa-ul small">
              <li>
                <span className="fa fa-check fa-li color-green" />
                {I18n.t('js.productBundles.productBundleForm.advertised')}
              </li>
              <li>
                <span className="fa fa-check fa-li color-green" />
                {I18n.t('js.productBundles.productBundleForm.discount')}
              </li>
              <li>
                <span className="fa fa-check fa-li color-green" />
                {I18n.t('js.productBundles.productBundleForm.directCheckout')}
              </li>
              <li>
                <span className="fa fa-check fa-li color-green" />
                <a href={this.props.learnMoreBundlePath} target="blank">
                  {I18n.t('js.productBundles.productBundleForm.learnMore')}
                </a>
              </li>
            </ul>
            {this.renderPreview()}
          </div>
        </div>
      </CSRFForm>
    );
  }
}

export default ProductBundleForm;



// WEBPACK FOOTER //
// ./javascripts/components/product_bundles/product_bundle_form.jsx